import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import { Meal, IngredientV2 } from '@llm-macro-tracker/shared';
import { format, addDays, subDays } from 'date-fns';
import MealSkeleton from './MealSkeleton';
import MacroSkeleton from './MacroSkeleton';
import { AddMealModal } from './AddMealModal';
import { Link } from 'react-router-dom';
import { UserProfile } from '@llm-macro-tracker/shared';
import { MacroDisplay } from './MacroDisplay';
import { FaUserCog, FaSignOutAlt, FaCommentAlt } from 'react-icons/fa';
import { AnalyticsService } from 'services/firebaseService';
import { QuickMealInput } from './QuickMealInput';
import { motion, AnimatePresence } from 'framer-motion';

const MealItem: React.FC<{
  meal: Meal & { id: string };
  onEdit: () => void;
  onDelete: () => void;
  onToggleExpand: () => void;
  isExpanded: boolean;
}> = ({ meal, onEdit, onDelete, onToggleExpand, isExpanded }) => (
  <motion.li
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3 }}
    className="bg-white border-b border-gray-200 last:border-b-0 transition-all duration-300"
  >
    <div className="p-3 cursor-pointer" onClick={onToggleExpand}>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center space-x-2 flex-grow min-w-0">
          <h3 className="text-sm font-medium text-gray-700 truncate">{meal.mealName}</h3>
        </div>
        <div className="flex space-x-2 flex-shrink-0 ml-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="text-gray-500 hover:text-gray-700 transition duration-300 p-1"
            aria-label="Edit meal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-gray-500 hover:text-gray-700 transition duration-300 p-1"
            aria-label="Delete meal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
      <div className="flex flex-wrap justify-center sm:justify-start gap-2">
        <MacroPill
          icon="🔥"
          value={meal.totalMacros.calories}
          color="bg-orange-100 text-orange-800"
        />
        <MacroPill icon="🥩" value={meal.totalMacros.protein} color="bg-red-100 text-red-800" />
        <MacroPill icon="🍞" value={meal.totalMacros.carbs} color="bg-yellow-100 text-yellow-800" />
        <MacroPill icon="🥑" value={meal.totalMacros.fat} color="bg-green-100 text-green-800" />
      </div>
    </div>
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          key="content"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="px-4 pb-4 overflow-hidden"
        >
          <p className="text-sm text-gray-600 mb-3">{meal.description}</p>
          <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Ingredients:</h4>
            <ul className="space-y-3">
              {meal.ingredients.map((ingredient: IngredientV2, index: number) => (
                <li key={index} className="text-sm flex flex-col sm:flex-row sm:items-start">
                  <div className="sm:w-1/3 mb-2 sm:mb-0">
                    <span className="text-gray-800 font-medium block">{ingredient.name}</span>
                    {ingredient.amount && (
                      <span className="text-gray-600 text-xs">{`${ingredient.amount} ${ingredient.unit}`}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-center sm:justify-end sm:w-2/3 gap-2">
                    <MacroPill
                      icon="🔥"
                      value={ingredient.calories}
                      color="bg-orange-100 text-orange-800"
                    />
                    <MacroPill
                      icon="🥩"
                      value={ingredient.protein}
                      color="bg-red-100 text-red-800"
                    />
                    <MacroPill
                      icon="🍞"
                      value={ingredient.carbs}
                      color="bg-yellow-100 text-yellow-800"
                    />
                    <MacroPill
                      icon="🥑"
                      value={ingredient.fat}
                      color="bg-green-100 text-green-800"
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.li>
);

const MacroPill: React.FC<{ icon: string; value: number; color: string }> = ({
  icon,
  value,
  color,
}) => (
  <div
    className={`${color} rounded-full px-2 py-1 text-xs font-semibold flex items-center justify-center shadow-sm w-16`}
  >
    <span>{icon}</span>
    <span className="tabular-nums ml-1">{Math.ceil(value).toString().padStart(4, '\u00A0')}</span>
  </div>
);

export const MealTracker: React.FC = () => {
  const [meals, setMeals] = useState<(Meal & { id: string })[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [expandedMealId, setExpandedMealId] = useState<string | null>(null);
  const [editingMeal, setEditingMeal] = useState<(Meal & { id: string }) | null>(null);
  const [isLoadingMeals, setIsLoadingMeals] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const { user, logout, getMealsForDate, addMeal, deleteMeal, updateMeal, getUserProfile } =
    useUser();
  const [isQuickInputOpen, setIsQuickInputOpen] = useState(false);

  const fetchMeals = useCallback(async () => {
    setIsLoadingMeals(true);
    try {
      setMeals(await getMealsForDate(selectedDate));
    } catch (error) {
      console.error('Error fetching meals:', error);
    } finally {
      setIsLoadingMeals(false);
    }
  }, [getMealsForDate, selectedDate]);

  const fetchUserProfile = useCallback(async () => {
    setIsLoadingProfile(true);
    if (user) {
      try {
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    }
  }, [user, getUserProfile]);

  useEffect(() => {
    fetchMeals();
    fetchUserProfile();
  }, [fetchMeals, fetchUserProfile]);

  useEffect(() => {
    if (isQuickInputOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [isQuickInputOpen]);

  const handleAddOrUpdateMeal = async (newMeal: Omit<Meal, 'userId' | 'timestamp' | 'v'>) => {
    if (user) {
      try {
        if (editingMeal) {
          await updateMeal(editingMeal.id, newMeal);
          AnalyticsService.logEvent('meal_updated', { mealId: editingMeal.id });
        } else {
          await addMeal(newMeal);
          AnalyticsService.logEvent('meal_added');
        }
        await fetchMeals();
        setIsModalOpen(false);
        setEditingMeal(null);
      } catch (error) {
        console.error('Error adding/updating meal:', error);
      }
    }
  };

  const handleDeleteMeal = async (mealId: string) => {
    await deleteMeal(mealId);
    fetchMeals();
  };

  const toggleMealExpansion = (mealId: string) => {
    setExpandedMealId((prevId) => (prevId === mealId ? null : mealId));
  };

  const totalMacros = meals.reduce(
    (acc, meal) => ({
      calories: acc.calories + meal.totalMacros.calories,
      protein: acc.protein + meal.totalMacros.protein,
      carbs: acc.carbs + meal.totalMacros.carbs,
      fat: acc.fat + meal.totalMacros.fat,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 },
  );

  const handleDateChange = (direction: 'prev' | 'next') => {
    setSelectedDate((prevDate) =>
      direction === 'prev' ? subDays(prevDate, 1) : addDays(prevDate, 1),
    );
  };

  const handleEditMeal = (meal: Meal & { id: string }) => {
    console.log('Setting editingMeal for edit', meal);
    setEditingMeal(meal);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    console.log('Closing modal and clearing editingMeal');
    setIsModalOpen(false);
    setEditingMeal(null); // Clear the editingMeal state
  };

  const defaultGoals = {
    dailyCalorieGoal: 2000,
    macroGoals: { protein: 30, carbs: 40, fat: 30 },
  };

  const handleQuickMealSubmit = async (analyzedMeal: any) => {
    if (analyzedMeal) {
      try {
        await addMeal({
          mealName: analyzedMeal.mealTitle,
          description: analyzedMeal.mealDescription,
          totalMacros: analyzedMeal.totalMacros,
          ingredients: analyzedMeal.items,
          date: selectedDate,
        });
        await fetchMeals();
        setIsQuickInputOpen(false);
      } catch (error) {
        console.error('Error adding meal:', error);
      }
    }
  };

  const isLoading = isLoadingMeals || isLoadingProfile;

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <header className="bg-white shadow-md top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo Section */}
          <Link to="/" className="flex items-center">
            <span role="img" aria-label="Plate" className="text-2xl mr-2">
              🍽️
            </span>
            <span className="text-xl font-semibold text-gray-800">MealTracker</span>
          </Link>

          {/* Navigation Links */}

          <nav className="flex items-center space-x-4">
            <button
              onClick={() => {
                // @ts-ignore - Tally types are not available
                window.Tally?.openPopup('mKP1Jk', {
                  width: 400,
                  autoClose: 3000,
                  hiddenFields: {
                    email: user?.email || 'unknown',
                    userId: user?.uid || 'unknown',
                    source: 'header_feedback',
                  },
                });
              }}
              className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors duration-300"
            >
              <FaCommentAlt className="h-5 w-5 mr-1" />
              <span className="hidden sm:inline">Feedback</span>
            </button>
            <Link
              to="/profile"
              className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors duration-300"
            >
              <FaUserCog className="h-5 w-5 mr-1" />
              <span className="hidden sm:inline">Goals</span>
            </Link>
            <button
              onClick={logout}
              className="flex items-center text-gray-600 hover:text-red-600 transition-colors duration-300"
            >
              <FaSignOutAlt className="h-5 w-5 mr-1" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>

      <motion.div
        className="flex-grow overflow-y-auto pb-16 "
        animate={{
          opacity: isQuickInputOpen ? 0.3 : 1,
          pointerEvents: isQuickInputOpen ? 'none' : 'auto',
        }}
        transition={{ duration: 0.2 }}
      >
        <div className="md:container md:mx-auto md:px-4 md:py-6 bg-gradient-to-r from-indigo-100 to-purple-100">
          <div className="bg-white p-4 md:p-6 mb-2 md:rounded-xl">
            {' '}
            {/* Reduced bottom margin */}
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={() => handleDateChange('prev')}
                className="text-indigo-600 hover:text-indigo-800 transition duration-300"
              >
                ← Previous Day
              </button>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                {format(selectedDate, 'MMM d, yyyy')}
              </h2>
              <button
                onClick={() => handleDateChange('next')}
                className="text-indigo-600 hover:text-indigo-800 transition duration-300"
              >
                Next Day →
              </button>
            </div>
            {isLoading ? (
              <>
                <MacroSkeleton />
                <div className="bg-white rounded-lg shadow-md p-4 mt-6">
                  <h2 className="text-xl font-semibold mb-3 text-gray-800">🍳 Meals</h2>
                  <MealSkeleton />
                  <MealSkeleton />
                  <MealSkeleton />
                </div>
              </>
            ) : (
              <>
                <MacroDisplay
                  macros={totalMacros}
                  goals={
                    userProfile
                      ? {
                          dailyCalorieGoal: userProfile.dailyCalorieGoal,
                          macroGoals: userProfile.macroGoals,
                        }
                      : defaultGoals
                  }
                />
                <div className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
                  {' '}
                  {/* Added mt-6 class */}
                  <h2 className="text-xl font-semibold p-4 border-b border-gray-200 text-gray-800">
                    🍳 Meals
                  </h2>
                  {meals.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {meals.map((meal) => (
                        <MealItem
                          key={meal.id}
                          meal={meal}
                          onEdit={() => handleEditMeal(meal)}
                          onDelete={() => handleDeleteMeal(meal.id)}
                          onToggleExpand={() => toggleMealExpansion(meal.id)}
                          isExpanded={expandedMealId === meal.id}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-600 text-center py-8">No meals logged for this date.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Position QuickMealInput at the bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        {' '}
        {/* Added border-top, removed shadow */}
        <QuickMealInput
          onSubmit={handleQuickMealSubmit}
          isOpen={isQuickInputOpen}
          setIsOpen={setIsQuickInputOpen}
        />
      </div>

      {isModalOpen && (
        <AddMealModal
          onClose={handleCloseModal}
          onAddOrUpdateMeal={handleAddOrUpdateMeal}
          selectedDate={selectedDate}
          editingMeal={editingMeal}
        />
      )}
    </div>
  );
};

export default MealTracker;
